import { Formik } from "formik";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Box, LinearProgress } from "@mui/material";

import PageContainer from "@components/PageContainer";
import { SlidesStepperNavButtons, SlideStepper, SlideStepperBreadcrumbs } from "@components/Slides/SlideStepper";

import { AttachmentType, CampaignType, EmailType } from "@interfaces/campaign.type";

import CampaignOverlay from "@pages/CreateCampaign/components/CampaignOverlay";
import campaignValidationSchema from "@pages/CreateCampaign/config/campaign.schema";
import useCreateCampaignSlides from "@pages/CreateCampaign/config/useCreateCampaignSlides";

import {
  useCampaignQuery,
  useHandleCampaignAttachments,
  useRemoveAttachmentQuery,
  useSendCampaignQuery,
  useUpdateCampaignQuery,
} from "@queries/campaign.query";

import CampaignService from "@services/campaign.service";
import UserService from "@services/user.service";

import { usePopupActions } from "@store/popupStore";
import { useUserStore } from "@store/userStore";

import contentCardStyles from "@theme/contentCardStyles";

import { sanitizeHtmlInput } from "@utilities/sanitizeHtmlInput";

import CreateCampaignStepper from "./components/CampaignStepper";
import ConnectEmailAccountForm from "./components/ConnectEmailAccountForm";
import EditNameAndDescription from "./components/EditNameAndDescription";
import CreateCampaignPersonalize from "./components/EditPersonalizedEmails";
import EditReviewAndTest from "./components/EditReviewAndTest/EditReviewAndTest";
import EditTemplate from "./components/EditTemplate";
import EditUserDetails from "./components/EditUserDetails";
import SentConfirmation from "./components/SentConfirmation";

export interface ICampaignFormValues {
  attachments: AttachmentType[];
  emailSignature: string;
  emailSubject: string;
  emailTemplate: string;
  jobTitle: string;
  name: string;
  organizationName: string;
  personalizedEmailsForContacts: EmailType[];
  technologySummary: string;
}

function CreateCampaign() {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams();
  const user = useUserStore();
  const PopupActions = usePopupActions();
  const slides = useCreateCampaignSlides();

  // needed to prevent campaign from affecting steps
  const initialLoad = useRef(true);

  const { data: campaign } = useCampaignQuery(campaignId);
  const { mutateAsync: updateCampaign } = useUpdateCampaignQuery();
  const { mutateAsync: sendCampaign, status: sendingCampaignStatus } = useSendCampaignQuery();
  const { mutateAsync: handleAttachments } = useHandleCampaignAttachments();
  const { mutateAsync: removeAttachment } = useRemoveAttachmentQuery();

  const deleteAttachment = async (attachmentId: number, attachmentName: string) => {
    if (!campaignId) return;
    await removeAttachment({ attachmentId, attachmentName, campaignId });
  };

  const calculateCampaignSteps = (campaign: CampaignType) => {
    // if campaign has sent or scheduled we can move campaign performance page
    if (campaign.sentAt || campaign.scheduledAt) {
      return navigate(`campaigns/${campaignId}/performance`);
    }
    // if campaign has emails generated we can move to personalize step
    if (campaign.emails.length > 0) {
      return "personalize";
    }
    // if campaign has name, we can move to compose step
    if (campaign.name) {
      return "compose";
    }
  };

  const authenticateEmail = useCallback(() => {
    PopupActions.openModal({
      content: <ConnectEmailAccountForm />,
      title: "Connect Your Email Account",
    });
  }, [PopupActions]);

  const handleCampaignNavigate = useCallback(
    (splatString: string) => {
      navigate(`/campaigns/create/${campaignId}/${splatString}`, { replace: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignId],
  );

  const breadcrumbs = useMemo(
    () => [
      {
        clickable: true,
        icon: LightbulbRoundedIcon,
        label: "Technology",
        onClick: () => navigate(`/portfolio/technology/${campaign?.technologyId}/companies`),
        tooltipText: "View your technology portfolio.",
      },
      {
        clickable: false,
        icon: SendRoundedIcon,
        label: "Create Campaign",
        onClick: () => navigate(`/portfolio/technology/${campaign?.technologyId}/campaigns`),
        tooltipText: "Create a campaign for this technology.",
      },
    ],
    [campaign?.technologyId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const slideIndex = useMemo(
    () => slides.findIndex((slide) => location.pathname.includes(slide.splatString)),
    [location.pathname, slides],
  );

  useEffect(() => {
    if (campaign && campaign?.sentAt) {
      navigate(`/campaigns/create/${campaign?.id}/confirmation`);
    }
  }, [campaign, navigate]);

  useEffect(() => {
    if (!campaign) return;
    if (!initialLoad.current) return;
    const lastStep = calculateCampaignSteps(campaign);
    initialLoad.current = false;

    if (lastStep) {
      navigate(`/campaigns/create/${campaignId}/${lastStep}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, campaign]);

  useEffect(() => {
    if (!user.nylasAuthenticated && slideIndex === 4) {
      authenticateEmail();
    }
  }, [user.nylasAuthenticated, authenticateEmail, slideIndex]);

  const handleBack = () => {
    if (slideIndex === 0) {
      navigate(campaign ? `/portfolio/technology/${campaign.technologyId}/campaigns` : "/portfolio", { replace: true });
    } else {
      const newSlideIndex = Math.max(slideIndex - 1, 0);
      const slideToNavigate = slides[newSlideIndex]!.splatString;
      handleCampaignNavigate(slideToNavigate);
    }
  };

  const saveSetupFields = async (values: Partial<ICampaignFormValues>) => {
    if (campaign && (campaign.technologySummary !== values.technologySummary || campaign.name !== values.name)) {
      await updateCampaign({
        id: campaign.id,
        method: "updateCampaign",
        name: values.name,
        technologySummary: values.technologySummary,
      });
    }
  };

  const handleFormSubmission = async (
    values: Partial<ICampaignFormValues>,
    setSubmitting: (argument: boolean) => void,
  ) => {
    let shouldNavigate = true;
    if (!campaign) return;
    try {
      setSubmitting(true);
      switch (slideIndex) {
        case 0: {
          // Account
          if (
            (user.emailSignature !== values.emailSignature ||
              user.organizationName !== values.organizationName ||
              user.jobTitle !== values.jobTitle) &&
            user.id
          ) {
            await UserService.update(user.id, {
              emailSignature: sanitizeHtmlInput(values.emailSignature),
              jobTitle: values.jobTitle,
              organizationName: values.organizationName,
            });
          }
          break;
        }
        case 1: {
          // Setup
          await saveSetupFields(values);
          break;
        }
        case 2: {
          // Compose
          //TODO: This Should be merged into one API call
          if (
            campaign.emailTemplate !== values.emailTemplate ||
            campaign.emailSubject !== values.emailSubject ||
            campaign.emails.length === 0 // initial case
          ) {
            await updateCampaign({
              emailSubject: values.emailSubject,
              emailTemplate: sanitizeHtmlInput(values.emailTemplate),
              id: campaign.id,
              method: "updateCampaign",
            });
            await updateCampaign({
              id: campaign.id,
              method: "generateEmails",
            });
          }
          break;
        }
        case 3: {
          // Personalize
          await updateCampaign({
            emails: values.personalizedEmailsForContacts,
            method: "saveEmails",
          });
          break;
        }
        case 4: {
          // Review
          if (!user.nylasAuthenticated) {
            shouldNavigate = false;
            authenticateEmail();
            return;
          }
          await sendCampaign({ campaignId: campaign.id, technologyId: campaign.technologyId });
          break;
        }
        case 5: {
          // Confirmation
          navigate(campaign ? `/portfolio/technology/${campaign.technologyId}/campaigns` : "/portfolio", {
            replace: true,
          });
          break;
        }
        default:
      }
    } catch {
      shouldNavigate = false;
    } finally {
      setSubmitting(false);
      if (shouldNavigate && slideIndex < 5) {
        handleCampaignNavigate(slides[slideIndex + 1]!.splatString);
      }
    }
  };

  const generateTemplate = async (id: number) => {
    if (campaign && !campaign.emailTemplate) {
      switch (id) {
        case 1: {
          // Create New Template
          await updateCampaign({
            id: campaign.id,
            method: "applyNewTemplate",
          });
          break;
        }
        case 2: {
          // Use Saved Template
          await updateCampaign({
            id: campaign.id,
            method: "applySavedTemplate",
          });
          break;
        }
        case 3: {
          // Use ScaleIP Template
          await updateCampaign({
            id: campaign.id,
            method: "applyStandardTemplate",
          });
          break;
        }
        default: {
          break;
        }
      }
      await updateCampaign({
        id: campaign.id,
        method: "generateEmails",
      });
    }
  };

  const validateForm = async (fields: string[], values: ICampaignFormValues) => {
    const errors: Record<string, string> = {};

    await Promise.all(
      fields.map(async (field) => {
        try {
          await (Yup.reach(campaignValidationSchema, field) as Yup.Schema).validate(
            values[field as keyof typeof values],
          );
          // Special case for emailTemplate empty state
          if (field === "emailTemplate" && values[field] === "<p><br></p>") {
            errors[field] = "Email template is required.";
          }
          if (field === "emailSignature" && values[field] === "<p><br></p>") {
            errors[field] = "Email signature is required.";
          }
        } catch (error) {
          const typedError = error as Yup.ValidationError;
          errors[field] = typedError.message;
        }
      }),
    );

    return errors;
  };

  const sortedArray = useMemo<EmailType[]>(() => {
    const groupedByCompany = _.groupBy(campaign?.emails, "organization.name");

    const sortedGroups = _.mapValues(groupedByCompany, (group) => {
      return _.orderBy(
        group,
        (person) => {
          if (!person.name) return "";
          return person.name.split(" ").at(-1);
        },
        ["asc"],
      );
    });

    const sortedCompanyNames = _.sortBy(Object.keys(groupedByCompany));
    return _.flatMap(sortedCompanyNames, (company) => sortedGroups[company]).filter((email) => email !== undefined);
  }, [campaign?.emails]);

  return (
    <PageContainer>
      <CampaignOverlay sendingCampaignStatus={sendingCampaignStatus} />
      {campaign ? (
        <Formik
          enableReinitialize
          // TODO: make all user and campaign fields empty strings by default and remove || "" from initialValues
          initialValues={{
            attachments: campaign.attachments,
            emailSignature: user.emailSignature ? sanitizeHtmlInput(user.emailSignature) : "",
            emailSubject: campaign.emailSubject,
            emailTemplate: sanitizeHtmlInput(campaign.emailTemplate),
            jobTitle: user.jobTitle || "",
            name: campaign.name || "",
            organizationName: user.organizationName || "",
            personalizedEmailsForContacts: sortedArray.map((email) => ({
              company: email.company,
              email: email.email,
              emailBody: sanitizeHtmlInput(email.emailBody),
              emailSubject: email.emailSubject,
              id: email.id,
              imgSrc: email.imgSrc,
              linkedinUrl: email.linkedinUrl,
              name: email.name,
              originalBody: sanitizeHtmlInput(email.originalBody),
              originalSubject: email.originalSubject,
              personalized: email.personalized,
              title: email.title,
            })),

            technologySummary: campaign.technologySummary || "",
          }}
          onSubmit={() => {
            /* Do Nothing */
          }}
          validationSchema={campaignValidationSchema}
        >
          {({ isSubmitting, setErrors, setSubmitting, setTouched, values }) => {
            const uploadAttachment = async (files: File[]) => {
              if (!campaignId) return;

              //Update campaign to preserve name and technology summary
              await updateCampaign({
                id: campaign.id,
                method: "updateCampaign",
                name: values.name,
                technologySummary: values.technologySummary,
              });

              await handleAttachments({
                campaignId,
                fileObjects: files.map((file) => ({
                  mimetype: file.type,
                  originalName: file.name,
                  size: file.size,
                })),
                files,
              });
            };

            const handleNext = async () => {
              const errors = await validateForm(
                slides[slideIndex]!.slideFields,
                values as unknown as ICampaignFormValues,
              );

              if (Object.keys(errors).length === 0) {
                await handleFormSubmission(values as unknown as ICampaignFormValues, setSubmitting);
              } else {
                PopupActions.openAlert({
                  message: "Please fill out all required fields.",
                  severity: "error",
                });

                // Must set touched to true for fields to display error messages
                const touched: Record<string, boolean> = {};
                for (const key of Object.keys(errors)) {
                  touched[key] = true;
                }
                await setTouched(touched);

                setErrors(errors);
              }
            };

            if (slideIndex < 0 || slideIndex > 5) return;

            const slide = slides[slideIndex]!;

            return (
              <>
                <SlideStepper>
                  <SlideStepperBreadcrumbs
                    breadcrumbs={breadcrumbs}
                    handleClickIndex={(index) => breadcrumbs[index]?.onClick()}
                    index={1}
                    workflow="campaign"
                  />
                  <SlidesStepperNavButtons
                    backButtonText=""
                    backButtonTooltip={slide.backButtonTooltip}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    isBackButtonDisabled={slide.isBackButtonDisabled}
                    isNextButtonDisabled={slide.isNextButtonDisabled}
                    isNextButtonPending={isSubmitting}
                    nextButtonText={slide.nextButtonText}
                    nextButtonTooltip={slide.nextButtonTooltip}
                  />
                </SlideStepper>

                <Box
                  sx={{
                    width: "100%",
                    ...contentCardStyles,
                    alignItems: "center",
                    px: slideIndex === 2 || slideIndex === 3 ? 0 : 4,
                  }}
                >
                  {slideIndex !== 5 && <CreateCampaignStepper activeStep={slideIndex} campaign={campaign} />}

                  <Routes>
                    <Route element={<EditUserDetails />} path="account" />
                    <Route
                      element={
                        <EditNameAndDescription
                          deleteAttachment={deleteAttachment}
                          uploadAttachment={uploadAttachment}
                        />
                      }
                      path="setup"
                    />
                    <Route element={<EditTemplate generateTemplate={generateTemplate} />} path="compose" />
                    <Route element={<CreateCampaignPersonalize />} path="personalize" />
                    <Route
                      element={
                        <EditReviewAndTest
                          campaignEmails={campaign.emails}
                          sendTestEmail={async (testEmailAddress: string) =>
                            CampaignService.sendTestEmail(campaign.id, testEmailAddress)
                          }
                          showAuthenticateEmail={authenticateEmail}
                        />
                      }
                      path="review"
                    />
                    <Route
                      element={
                        <SentConfirmation
                          campaignId={campaign.id}
                          isSuccess={!campaign.failedToSend}
                          technologyId={campaign.technologyId}
                        />
                      }
                      path="confirmation"
                    />
                  </Routes>
                </Box>
              </>
            );
          }}
        </Formik>
      ) : (
        <LinearProgress />
      )}
    </PageContainer>
  );
}

export default CreateCampaign;
